/** Treat empty editor values as absent so optional numeric rules do not see NaN. */
export function coerceEmptyToUndefined(value: unknown): unknown {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  return value;
}

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function coerceGuidValue(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return coerceGuidValue((value as { value: unknown }).value);
  }
  const str = String(value).trim();
  return GUID_PATTERN.test(str) ? str : null;
}

export function isFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (coerceGuidValue(value) != null) return true;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') return value.trim() !== '' && value !== '0';
  if (Array.isArray(value)) return value.length > 0;
  return false;
}

export interface ValidationCoalesceRule {
  /** Flat row field to populate when empty. */
  field: string;
  /** Dot path on the row, e.g. `settlementConfiguration.windowStart`. */
  from: string;
}

function readDotPath(row: Record<string, unknown>, path: string): unknown {
  const segments = path.split('.');
  let current: unknown = row;
  for (const segment of segments) {
    if (current == null || typeof current !== 'object') return undefined;
    if (Array.isArray(current)) {
      const collected = current
        .map((item) =>
          item != null && typeof item === 'object'
            ? (item as Record<string, unknown>)[segment]
            : undefined,
        )
        .filter((value) => value != null && value !== '');
      return collected.length > 0 ? collected : undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/**
 * Align validation input with nested DTO fields editors may not have flattened yet.
 * Only fills target fields when the primary value is empty.
 */
export function coalesceValidationRow(
  row: Record<string, unknown>,
  rules: ReadonlyArray<ValidationCoalesceRule> | null | undefined,
): Record<string, unknown> {
  if (!rules?.length) return row;

  const next = { ...row };
  for (const rule of rules) {
    if (isFieldFilled(next[rule.field])) continue;
    const fallback = readDotPath(row, rule.from);
    if (isFieldFilled(fallback)) {
      next[rule.field] = fallback;
    }
  }
  return next;
}
